import { getLogger } from '../../diagnostics/logger';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { ENSURE_PORT_NETWORKS, INITIAL_AUTHENTICATION, LOAD_PORT_NETWORKS } from '../../actions/actionTypes';
import { Action, PromiseAction } from '../../actions/commonActions';
import {
  loadPortNetworksFailure,
  loadPortNetworksPending,
  loadPortNetworksSuccess,
} from '../../actions/portNetworkActions';
import * as targetNetworkApi from '../../api/targetNetworkApi';
import { DropdownItem } from '../../components/ui/input/DropdownInput';
import { DropdownItemsState } from '../../models/state/simProvision/simProvisionState';
import { selectPortNetworks } from '../../selectors/portOut';

const log = getLogger('portNetworks.sagas');

/*
 * A saga which fetches the list of available networks to port to.
 */
export function* loadPortNetworksSaga(action: PromiseAction<number, DropdownItem[]>) {
  const { payload } = action;
  if (!payload) {
    return;
  }

  yield put(loadPortNetworksPending(payload));
  try {
    const networks: DropdownItem[] = yield call(targetNetworkApi.getPortNetworks, payload);
    yield put(loadPortNetworksSuccess(payload, networks));
    action.resolve && action.resolve(networks);
  } catch (error: any) {
    yield put(loadPortNetworksFailure(payload, error));
    log.error(error);
  }
}

/*
 * A saga which fetches the list of available networks to port to, if needed.
 */
export function* ensurePortNetworksSaga(action: Action<number>) {
  const networkId = action.payload;
  if (!networkId) {
    return;
  }

  const portNetworksState: DropdownItemsState = yield select(selectPortNetworks);
  const portNetworks = portNetworksState[networkId];
  if (portNetworks) {
    const { expiresOn, isLoading } = portNetworks;
    if (isLoading || (expiresOn && expiresOn > new Date())) {
      return;
    }
  }

  yield call(loadPortNetworksSaga, action);
}

/*
 * Initializes all sagas that are related to port networks
 */
export function* initPortNetworksSagas() {
  yield take(INITIAL_AUTHENTICATION);
  yield takeEvery(ENSURE_PORT_NETWORKS, ensurePortNetworksSaga);
  yield takeEvery(LOAD_PORT_NETWORKS, loadPortNetworksSaga);
}
